import React, { useState, useEffect, useCallback, useMemo } from "react";
import { TextInput } from "@alm/flow-ds/components";
import { CommonPropertyProps } from "schemas/elements";
import { TextValidation, NumberValidation } from "validation/validation";
import { useTranslation } from "react-i18next";

type Props = Omit<CommonPropertyProps, "value"> & {
	type?: string;
	value?: string | number | { [k: string]: string | number };
	nextProp?: string;
	format?: (input: string) => void;
	json?: boolean;
	setElemTitle?: (val: string) => void;
};

export default function TextContainer({
	updateStore,
	field,
	required,
	value,
	desc,
	validation,
	elementIndex,
	type,
	nextProp,
	format,
	json,
	id,
	setElemTitle,
	nestedField,
}: Props) {
	const { t } = useTranslation();

	const initValue = useMemo(() => {
		if (typeof value === "object" && nextProp) return value[nextProp];
		else return value;
	}, [nextProp, value]);

	const [textInputVal, setTextInputVal] = useState(
		initValue && json ? JSON.stringify(initValue) : initValue
	);

	const [isValid, setIsValid] = useState(true);

	const validateProperty = useCallback(() => {
		const updateValue =
			nextProp && typeof value === "object"
				? {
						...value,
						[nextProp]: format
							? format(textInputVal as string)
							: textInputVal,
				  }
				: format
				? format(textInputVal as string)
				: textInputVal;
		const valueToValidate =
			nextProp && typeof updateValue === "object"
				? updateValue[nextProp]
				: updateValue;
		const newValidation =
			type === "number"
				? !NumberValidation(valueToValidate as number)
				: !TextValidation(valueToValidate as string);
		setIsValid(!newValidation);
		return updateValue;
	}, [format, nextProp, textInputVal, type, value]);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const edited =
				type === "number"
					? e.target.value.replace("-", "")
					: e.target.value;
			setTextInputVal(edited);

			// setting the label for element
			if (setElemTitle) {
				setElemTitle(edited);
			}
		},
		[setElemTitle, type]
	);

	useEffect(() => {
		validateProperty();
	}, [validateProperty]);

	const handleBlur = useCallback(() => {
		const updateValue = validateProperty();
		updateStore({
			elementIndex,
			property: field,
			nestedField,
			value: updateValue,
		});
	}, [elementIndex, field, nestedField, updateStore, validateProperty]);

	return (
		<TextInput
			hint={t(`formDesigner.elements.${desc}`)}
			minNumber={0}
			id={id}
			type={type === "regex" ? undefined : type}
			value={textInputVal as string}
			required={required}
			label={
				nextProp
					? t(`formDesigner.properties.${nextProp}`)
					: t(`formDesigner.properties.${field}`)
			}
			onChange={handleChange}
			onBlur={handleBlur}
			status={validation && required && !isValid ? "error" : undefined}
		/>
	);
}
